<template>
	<nta-not-found v-if="notFound" :symbol-lang="domainStore.languagePreference" />
	<nta-page-layout v-else-if="!displayResponsive" hover-header>
		<template #title></template>
		<template #default>
			<TabsContainer style="width: calc(100% - var(--questions-sidebar-width))">
				<template #toolbar>
					<v-icon
						v-if="showBackButton()"
						v-ripple
						class="back-button"
						size="24"
						@click="
							router.push(
								'/' +
									domainStore.domain.id +
									'/template/' +
									instanceDataspacesStore.openedDataspaceId +
									'/template/workspaces',
							)
						"
						>mdi-chevron-left</v-icon
					>
					<span class="toolbar-workspace-label">
						{{ instanceWorkspacesStore.openedWorkspace?.label }}
					</span>
				</template>
				<template #overlay="{ activeTab }">
					<div
						v-if="activeTab === 0"
						style="pointer-events: none; position: absolute; top: 10px; left: 10px; z-index: 100"
					>
						<v-btn
							icon="mdi-image-filter-center-focus"
							variant="text"
							style="pointer-events: auto"
							@click="centerGraph"
						></v-btn>
					</div>
				</template>
				<template #graph>
					<GraphlyD3
						ref="graphly"
						:dark="darkTheme"
						:env-gravity="0"
						style="width: 100%; height: 100%"
						@node-click="nodeClick"
						@node-double-click="nodeDoubleClick"
						@node-drag-move="nodeDragMove"
						@node-drag-end="nodeDragEnd"
						@node-context-menu="nodeContextMenu"
						@environment-click="environmentClick"
						@environment-move="environmentMove"
					/>
				</template>
			</TabsContainer>
			<toast-notifications />
			<div v-if="workspaceLoaded">
				<DatabaseImport
					:show-modal="importModal.show"
					:template-id="importModal.templateId"
					:parent-instance-dataobject-id="importModal.parentId"
					@create-new="suggestionClick"
					@close="
						importModal.show = false;
						importModal.parentId = '';
						importModal.templateId = '';
					"
				/>
				<!-- <Snackbars /> -->
				<dataobject-context-menu
					:show="contextData.show"
					:dataobject-id="contextData.dataobjectId"
					@remove="contextData.show = false"
				/>
				<AlternisNewSidebar v-if="alternisNewStore.useNewAlternis" />
				<alternis-sidebar v-else />
				<questions-sidebar />
				<composed-attributes-modal
					v-if="composedAtributesData.show"
					:show="composedAtributesData.show"
					:dataobject-id="composedAtributesData.dataobjectId"
					parent-id=""
					:is-nested="false"
					@done="() => (composedAtributesData.show = false)"
				/>
				<notification-modal
					v-if="clickedNotificationSatellite"
					:notification="clickedNotificationSatellite"
					@done="clickedNotificationSatellite = undefined"
				/>
				<div style="position: absolute; bottom: 0; left: 71px; width: calc(100vw - 71px)">
					<v-progress-linear
						:active="instanceWorkspacesStore.progressActive"
						color="primary"
						indeterminate
					></v-progress-linear>
				</div>
			</div>
		</template>
	</nta-page-layout>
	<responsive-wrapper v-else>
		<template #dataentry>
			<questions-sidebar />
		</template>
		<template #chat>
			<AlternisNewSidebar v-if="alternisNewStore.useNewAlternis" />
			<alternis-sidebar v-else />
		</template>
		<template #graph>
			<mobile-graph-representation :graph="instanceWorkspacesStore.workspaceGraph" />
		</template>
		<dataobject-context-menu
			:show="contextData.show"
			:dataobject-id="contextData.dataobjectId"
			@remove="contextData.show = false"
		/>
		<notification-modal
			v-if="clickedNotificationSatellite"
			:notification="clickedNotificationSatellite"
			@done="clickedNotificationSatellite = undefined"
		/>
		<div style="position: absolute; bottom: 0; left: 71px; width: calc(100vw - 71px)">
			<v-progress-linear
				:active="instanceWorkspacesStore.progressActive"
				color="primary"
				indeterminate
			></v-progress-linear>
		</div>
	</responsive-wrapper>
</template>

<script setup lang="ts">
import { type Node, Event as GlyEvent } from "@livereader/graphly-d3";
import { select } from "d3-selection";
import { ref, onMounted, onUnmounted, computed, watch, onBeforeMount } from "vue";
import router from "../../../router";
import { api } from "@lr-notitia/notitia-operator/client";
import { useDomainStore } from "../../../store/domain";
import { useGraphStore } from "../../../store/graph";
import { useInstanceDataspacesStore } from "../../../store/i-dataspace";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";
import { useQuestionsStore } from "../../../store/questions";
import { useBetaStore } from "../../../store/beta";
import { useAlternisNewStore } from "../../../store/alternis_new";
import { useGptSuggestionsStore } from "../../../store/gpt/suggestions";
import { useSessionStore } from "../../../store/alternis/session";
import { api as alternisAPI } from "@lr-notitia/notitia-alternis/client";
import { onBeforeRouteLeave } from "vue-router";
import { useAudioPlayerStore } from "../../../store/alternis/audioPlayer";

import "@livereader/graphly-d3/style.css";
import { UserRole, DescriptorNotification } from "@lr-notitia/notitia-operator/schemas";
import { useResponsiveLayout } from "@lr-notitia/notitia-ui";

import GraphlyD3 from "@livereader/graphly-d3/component/vue3";
import AlternisSidebar from "../components/alternis/AlternisSidebar.vue";
import AlternisNewSidebar from "../components/alternis_new/AlternisSidebar.vue";
import QuestionsSidebar from "../components/QuestionsSidebar.vue";
import ComposedAttributesModal from "../components/ComposedAttributesModal.vue";
import DataobjectContextMenu from "../components/DataobjectContextMenu.vue";
import NotificationModal from "../components/NotificationModal.vue";
import ResponsiveWrapper from "../components/ResponsiveWrapper.vue";
import MobileGraphRepresentation from "../components/mobile_bubbly/MobileGraphRepresentation.vue";
import DatabaseImport from "../components/databaseImport/DatabaseImport.vue";
import ToastNotifications from "../components/ToastNotifications.vue";
import TabsContainer from "../components/tabs/TabsContainer.vue";
// import Snackbars from "../components/Snackbars.vue";

const domainStore = useDomainStore();
const graphStore = useGraphStore();
const instanceDataspacesStore = useInstanceDataspacesStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();
const questionsStore = useQuestionsStore();
const betaStore = useBetaStore();
const alternisNewStore = useAlternisNewStore();
const gptSuggestionsStore = useGptSuggestionsStore();
const sessionStore = useSessionStore();
const audioPlayerStore = useAudioPlayerStore();

const { displayResponsive } = useResponsiveLayout();

const composedAtributesData = ref<{ show: boolean; dataobjectId: string }>({ show: false, dataobjectId: "" });

const contextData = ref<{
	show: boolean;
	dataobjectId: string;
}>({
	show: false,
	dataobjectId: "",
});
const clickedNotificationSatellite = ref<DescriptorNotification>();

const workspaceLoaded = ref(false);
const notFound = computed(() => !domainStore.domain.id || !instanceWorkspacesStore.openedWorkspaceId);
const graphly = ref<any>(null);
const darkTheme = ref(false);
const worldScale = ref(1);
const worldBounds = ref<{ x: number; y: number; width: number; height: number }>({
	x: 0,
	y: 0,
	width: 0,
	height: 0,
});
const defaultPosSet = ref(false);

let domainId = router.currentRoute.value.params.domainId as string;
let workspaceId = router.currentRoute.value.params.workspaceId as string;
let betaCaseId = router.currentRoute.value.params.betaCaseId as string;
const presetId = router.currentRoute.value.params.presetId as string;

const importModal = ref({
	show: false,
	templateId: "",
	parentId: "",
});

const checkSessionIntervalRef = ref<NodeJS.Timeout | null>(null);
const inviteBotTimeoutRef = ref<NodeJS.Timeout | null>(null);

function centerGraph(): void {
	graphly.value.simulation.moveTo({
		nodes: instanceWorkspacesStore.workspaceGraph.nodes,
		padding: 50,
	});
}

async function render(): Promise<void> {
	if (!graphly.value?.simulation) return;
	await graphly.value.simulation.render(instanceWorkspacesStore.workspaceGraph, 0);
}

function nodeClick(event: any, node: Node) {
	contextData.value.show = false;
	if (node.id.includes("_suggestion") && !node.id.includes("_disabled")) {
		suggestionClick(node, node.payload.templateDataobjectId, node.payload.parentInstanceId);
		render();
	} else if (graphStore.descriptor.notification.byId(node.id)) {
		notificationSatelliteClick(node);
		render();
	} else if (!node.id.includes("_suggestion")) {
		instanceWorkspacesStore.selectedNodeId = node.id;
	}
}
function nodeDoubleClick(event: any, node: Node) {
	if (node.shape.type === "satellite-notification") return;
	if (!node.id.includes("_suggestion")) {
		composedAtributesData.value.show = true;
		composedAtributesData.value.dataobjectId = node.id;
	}
}
function nodeDragMove(event: any, node: Node, pos: { x: number; y: number }) {
	api()
		.socket()
		.domain(domainId)
		.instance()
		.workspace(workspaceId)
		.livePosition()
		.send({ [node.id]: { x: pos.x, y: pos.y, scale: node.shape.scale } });
}
function nodeDragEnd(event: any, node: Node, pos: { x: number; y: number }) {
	let previousPos = instanceWorkspacesStore.nodePositions[workspaceId][node.id];
	let dx = pos.x - previousPos?.x;
	let dy = pos.y - previousPos?.y;
	if (Math.abs(dx) < 30 && Math.abs(dy) < 30) return;
	if (!instanceWorkspacesStore.nodePositions[workspaceId]) instanceWorkspacesStore.nodePositions[workspaceId] = {};
	instanceWorkspacesStore.nodePositions[workspaceId][node.id] = {
		x: pos.x,
		y: pos.y,
		scale: node.shape.scale,
	};
	instanceWorkspacesStore.updateNodePositions({
		[node.id]: {
			x: pos.x,
			y: pos.y,
			scale: node.shape.scale,
		},
	});
}
function nodeContextMenu(event: any, node: Node) {
	// contextData.value.show = false;
	// event.preventDefault();
	// event.stopPropagation();
	// contextData.value = {
	// 	show: true,
	// 	dataobjectId: node.id,
	// };
}

async function suggestionClick(node: Node, templateId: string, parentInstanceId: string) {
	contextData.value.show = false;
	instanceWorkspacesStore.selectedNodeId = "";
	importModal.value.parentId = parentInstanceId;
	importModal.value.templateId = templateId;
	importModal.value.show = true;
}

function positionNewNode(node: Node): { [nodeId: string]: { x: number; y: number; scale: number } } | undefined {
	if (
		instanceWorkspacesStore.nodePositions[workspaceId]?.[node.id]?.x &&
		instanceWorkspacesStore.nodePositions[workspaceId]?.[node.id]?.y
	)
		return;
	let newPos = instanceWorkspacesStore.newNodePos(node.id);
	if (!instanceWorkspacesStore.nodePositions[workspaceId]) {
		instanceWorkspacesStore.nodePositions[workspaceId] = {};
	}
	instanceWorkspacesStore.nodePositions[workspaceId][node.id] = {
		x: newPos.x,
		y: newPos.y,
		scale: 1,
	};
	return {
		[node.id]: {
			x: newPos.x,
			y: newPos.y,
			scale: 1,
		},
	};
}

function environmentClick() {
	contextData.value.show = false;
	instanceWorkspacesStore.selectedNodeId = "";
}
function environmentMove(transform: { x: number; y: number; k: number }) {
	worldScale.value = transform.k;
	worldBounds.value = graphly.value.simulation.worldBounds;
}

function activateProgress() {
	instanceWorkspacesStore.progressActive = true;
	setTimeout(() => {
		deactivateProgress();
	}, 6000);
}
function deactivateProgress() {
	instanceWorkspacesStore.progressActive = false;
}

function notificationSatelliteClick(node: Node) {
	let notification = graphStore.descriptor.notification.byId(node.id);
	if (!notification) return;
	clickedNotificationSatellite.value = notification;
}

function observeTheme() {
	darkTheme.value = select("body").classed("dark");
	const themeObserver = new MutationObserver(() => {
		darkTheme.value = select("body").classed("dark");
	});
	themeObserver.observe(document.getElementsByTagName("body")[0], { attributes: true });
}

function setDefaultNodePositions(nodes: Node[]) {
	if (!instanceWorkspacesStore.nodePositions[workspaceId]) {
		setTimeout(() => {
			setDefaultNodePositions(nodes);
		}, 500);
		return;
	}
	let glyContainer = document.getElementsByClassName("gly-container");
	if (!glyContainer || glyContainer.length === 0) {
		setTimeout(() => {
			setDefaultNodePositions(nodes);
		}, 500);
		return;
	}

	nodes.forEach((node) => {
		if (node.x || node.y) return;
		if (
			instanceWorkspacesStore.nodePositions[workspaceId]?.[node.id]?.x &&
			instanceWorkspacesStore.nodePositions[workspaceId]?.[node.id]?.y
		)
			return;
		if (node.id.includes("_suggestion")) return;
		if (node.shape.type === "satellite-notification") return;

		let newPos = instanceWorkspacesStore.newNodePos(node.id);
		instanceWorkspacesStore.updateNodePositions({
			[node.id]: {
				x: newPos.x,
				y: newPos.y,
				scale: 1,
			},
		});
	});
	defaultPosSet.value = true;
	render();
	setTimeout(() => render(), 500);
}

async function inviteBotToSession(botId: string) {
	try {
		if (!sessionStore.sessionId) {
			console.warn("No session ID available for bot invitation");
			return;
		}

		const sessionBots = await alternisAPI().session().bots().get(sessionStore.sessionId);
		const botAlreadyInSession = sessionBots.some((bot) => bot.botId === botId);

		if (botAlreadyInSession) {
			console.log(`Bot ${botId} is already in session ${sessionStore.sessionId}`);
			return;
		}

		await alternisAPI().session().bots().post(sessionStore.sessionId, botId);
		console.log(`Bot ${botId} invited to session ${sessionStore.sessionId}`);
	} catch (error) {
		console.error("Error inviting bot to session:", error);
	}
}

function showBackButton() {
	// Only show the back button if the user navigated here from the Workspaces overview.
	const previousPath = (router.options.history.state as any)?.back as string | null | undefined;
	if (!previousPath) return false;
	const expectedBack =
		"/" + domainStore.domain.id + "/template/" + instanceDataspacesStore.openedDataspaceId + "/template/workspaces";
	if (!previousPath.startsWith(expectedBack)) return false;
	return true;
}

watch(
	() => graphly.value,
	async () => {
		observeTheme();
		const simulation = graphly.value.simulation;
		simulation.on(GlyEvent.NodeDragStart, (event: any, node: Node, position: any) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return;
			if (event.sourceEvent.altKey) return "newlink";
		});
		simulation.on("template:satellite-notification:icon-click", (node: Node, event: any) => {
			event.stopPropagation();
			if (!instanceWorkspacesStore.expandedNotifications.includes(node.id))
				instanceWorkspacesStore.expandedNotifications.push(node.id);
			else
				instanceWorkspacesStore.expandedNotifications = instanceWorkspacesStore.expandedNotifications.filter(
					(n) => n !== node.id,
				);
		});
		render();
	},
);

const initialCenterDone = ref(false);
watch(
	() => graphly.value?.simulation?.graph?.nodes?.length,
	(newVal, oldVal) => {
		if (initialCenterDone.value) return;
		if ((!oldVal || oldVal === 0) && newVal > 0) {
			initialCenterDone.value = true;
			setTimeout(() => {
				graphly.value.simulation.moveTo({
					nodes: instanceWorkspacesStore.workspaceGraph.nodes,
					padding: 50,
					duration: 400,
				});
			}, 1000);
		}
	},
);

watch(
	() => instanceWorkspacesStore.workspaceGraph,
	(newVal, oldVal) => {
		// initial positioning
		if ((!oldVal || oldVal.nodes.length === 0) && newVal.nodes.length > 0) {
			setDefaultNodePositions(instanceWorkspacesStore.workspaceGraph.nodes);
		}
		// position new added nodes - (to cover nodes added from function plugins)
		let shouldCenterAfterRender = false;
		if (oldVal && newVal.nodes.length > oldVal.nodes.length && defaultPosSet.value) {
			let newNodes = newVal.nodes.filter((n) => !oldVal.nodes.find((o) => o.id === n.id));
			if (newNodes.length > 0) {
				let addedMainNodes = 0;
				const batchedPositions: { [nodeId: string]: { x: number; y: number; scale: number } } = {};
				newNodes.forEach((node) => {
					if (node.satellite) return;
					addedMainNodes++;
					const update = positionNewNode(node);
					if (update) Object.assign(batchedPositions, update);
				});
				if (Object.keys(batchedPositions).length > 0) {
					instanceWorkspacesStore.updateNodePositions(batchedPositions);
				}
				if (addedMainNodes > 0) {
					shouldCenterAfterRender = true;
				}
			}
		}
		render().then(() => {
			if (shouldCenterAfterRender) {
				centerGraph();
			}
		});
	},
	{ deep: true, immediate: true },
);
watch(
	() => instanceWorkspacesStore.selectedNodeId,
	(val) => {
		if (val === "") graphly.value.simulation.selectedNodes = [];
		else graphly.value.simulation.selectedNodes = [val];
	},
);

watch(
	() => questionsStore.zoomToId,
	() => {
		if (!questionsStore.zoomToId) return;
		let node = instanceWorkspacesStore.workspaceGraph.nodes.find((n) => n.id === questionsStore.zoomToId);
		if (!node) return;
		const simulation = graphly.value.simulation;
		simulation.moveTo({
			nodes: [node],
			duration: 750,
			padding: 200,
		});
		questionsStore.zoomToId = "";
	},
);

onBeforeMount(async () => {
	workspaceLoaded.value = false;
	await instanceWorkspacesStore.loadWorkspace(domainStore.domain.id, workspaceId);
	workspaceLoaded.value = true;
});

onMounted(() => {
	questionsStore.tab = "open";
	if (presetId) {
		betaStore.writeBetaScenarioData(domainId, workspaceId, presetId);
	}
	api()
		.socket()
		.domain(domainId)
		.instance()
		.workspace(workspaceId)
		.livePosition()
		.on((positions) => {
			// live position for node while dragging
			if (Object.keys(positions).length === 1) {
				const node = instanceWorkspacesStore.workspaceGraph.nodes.find((node) => positions[node.id]);
				if (node) {
					node.x = positions[node.id]?.x || 0;
					node.y = positions[node.id]?.y || 0;
					node.shape.scale = positions[node.id].scale;
				}
			}
		});

	alternisNewStore.initSSE();
	setTimeout(() => {
		gptSuggestionsStore.initSSE();
	}, 3000);

	// Check for bot invitation from URL parameter
	const route = router.currentRoute.value;
	const botId = route.query.botId as string;

	if (botId) {
		// Wait for session to be created and loaded
		checkSessionIntervalRef.value = setInterval(() => {
			if (sessionStore.sessionId) {
				if (checkSessionIntervalRef.value) {
					clearInterval(checkSessionIntervalRef.value);
					checkSessionIntervalRef.value = null;
				}
				// Remove bot ID from URL to prevent re-invitation on reload
				router.replace({ query: { ...route.query, botId: undefined } });

				inviteBotTimeoutRef.value = setTimeout(() => {
					inviteBotToSession(botId);
					inviteBotTimeoutRef.value = null;
				}, 2000);
			}
		}, 500);

		// Clear interval after 30 seconds to prevent infinite checking
		setTimeout(() => {
			if (checkSessionIntervalRef.value) {
				clearInterval(checkSessionIntervalRef.value);
				checkSessionIntervalRef.value = null;
			}
		}, 30000);
	}
});
onUnmounted(() => {
	api().socket().domain(domainId).instance().workspace(workspaceId).livePosition().off();
	alternisNewStore.closeSSE();
	gptSuggestionsStore.closeSSE();

	if (checkSessionIntervalRef.value) {
		clearInterval(checkSessionIntervalRef.value);
		checkSessionIntervalRef.value = null;
	}
	if (inviteBotTimeoutRef.value) {
		clearTimeout(inviteBotTimeoutRef.value);
		inviteBotTimeoutRef.value = null;
	}
});

onBeforeRouteLeave(() => {
	// Stop all playing audio when leaving the workspace editor
	audioPlayerStore.audioInstances.forEach((_, audioId) => {
		audioPlayerStore.stop(audioId);
	});
});
</script>

<style lang="scss">
.gly-selectable.gly-selected {
	stroke: #8cd5f6 !important;
	stroke-width: 2px !important;
}

// lower opacity for disabled suggestions
.gly-node[data-id*="_suggestion"] {
	&:hover {
		cursor: copy;
	}
	&[data-id*="_disabled"] {
		opacity: 0.5;
		cursor: not-allowed;
	}
}

.toolbar-workspace-label {
	font-size: 1rem;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	padding: 0 4px;
	margin-bottom: -2px;
	align-self: center;
}

.back-button {
	cursor: pointer;
	padding: 16px !important;
	border-radius: 16px;
	align-self: center;
	position: relative;
	overflow: hidden;
	opacity: 0.7;
	transition: opacity 0.15s ease, background-color 0.15s ease;
	&:hover {
		opacity: 1;
		background-color: rgba(var(--v-theme-on-surface), 0.08);
	}
}
</style>
